/**
 * Validation utilities matching iOS implementation
 */

// App configuration interface for validation settings
interface ValidationConfig {
  minimumPasswordLength?: number;
  maximumPasswordLength?: number;
}

// Default validation config (matching iOS defaults)
const DEFAULT_VALIDATION_CONFIG: Required<ValidationConfig> = {
  minimumPasswordLength: 8,
  maximumPasswordLength: 128,
};

// Global validation config - can be updated from server config
let validationConfig: Required<ValidationConfig> = { ...DEFAULT_VALIDATION_CONFIG };

/**
 * Update validation configuration from server config
 * This matches the iOS implementation where config is fetched from server
 */
export const updateValidationConfig = (config: ValidationConfig): void => {
  validationConfig = {
    minimumPasswordLength: config.minimumPasswordLength ?? DEFAULT_VALIDATION_CONFIG.minimumPasswordLength,
    maximumPasswordLength: config.maximumPasswordLength ?? DEFAULT_VALIDATION_CONFIG.maximumPasswordLength,
  };
};

/**
 * Get current validation configuration
 */
export const getValidationConfig = (): Required<ValidationConfig> => {
  return { ...validationConfig };
};

/**
 * Email validation matching iOS implementation exactly
 * iOS pattern: "[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,64}"
 */
export const isEmailValid = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  try {
    // Exact pattern from iOS String+Extension.swift
    const emailRegex = /^[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}$/;
    return emailRegex.test(email);
  } catch (error) {
    return false;
  }
};

/**
 * Password validation matching iOS implementation exactly
 * iOS logic: Uses dynamic min/max length from server config
 */
export const isPasswordValid = (password: string): boolean => {
  if (!password || typeof password !== 'string') {
    return false;
  }

  const length = password.length;
  const { minimumPasswordLength, maximumPasswordLength } = validationConfig;

  return length >= minimumPasswordLength && length <= maximumPasswordLength;
};

/**
 * Name validation matching iOS implementation
 * iOS logic: name.count > 3
 */
export const isNameValid = (name: string): boolean => {
  if (!name || typeof name !== 'string') {
    return false;
  }

  return name.trim().length > 3;
};

/**
 * Get password validation requirements for user feedback
 */
export const getPasswordRequirements = (): { min: number; max: number } => {
  return {
    min: validationConfig.minimumPasswordLength,
    max: validationConfig.maximumPasswordLength,
  };
};

/**
 * Validate form fields and return specific error messages
 */
export const validateLoginForm = (email: string, password: string): string | null => {
  if (!isEmailValid(email)) {
    return 'Please enter a valid email address.';
  }

  if (!isPasswordValid(password)) {
    const { min, max } = getPasswordRequirements();
    return `Password must be between ${min} and ${max} characters.`;
  }

  return null;
};

/**
 * Validate signup form fields and return specific error messages
 */
export const validateSignupForm = (name: string, email: string): string | null => {
  if (!isNameValid(name)) {
    return 'Name must be at least 4 characters long.';
  }

  if (!isEmailValid(email)) {
    return 'Please enter a valid email address.';
  }

  return null;
};

/**
 * Validate forgot password form and return specific error messages
 */
export const validateForgotPasswordForm = (email: string): string | null => {
  if (!isEmailValid(email)) {
    return 'Please enter a valid email address.';
  }

  return null;
};
